import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Block, BuildCircle, FiberNew } from '@mui/icons-material'
import { Box, Button, Chip, CircularProgress, Divider, Grid, Stack, Typography } from '@mui/material'

import { AnimatedImage } from '../../../../../../components/AnimatedImage/AnimatedImage'
import { GRTooltip } from '../../../../../../components/GRTooltip/GRTooltip'
import { HtmlContentParser } from '../../../../../../components/HtmlContentParser/HtmlContentParser'
import { liveEventReviewModalParserOptions } from '../../../../../../components/LiveEventModalLink/LiveEventModalLink'
import { useDisplayExperimentalFeature, ExperimentalFeature } from '../../../../../../hooks/useDisplayExperimentalFeature'
import languageService from '../../../../../../services/LanguageService'
import { colors } from '../../../../../../themes/theme'
import { useInternalAccessCheck } from '../../../../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../../../../account/hooks/useCurrentUserLanguage'
import { MotivationTypeBadge } from '../../../../../motivations/components/MotivationTypeBadge/MotivationTypeBadge'
import { liveEventPriceRange } from '../../../../hooks/liveEventPriceRange'
import { useLiveEventBlueprintName } from '../../../../hooks/liveEventsBlueprints'
import { useBlueprintFamilyForEvent } from '../../../../hooks/useBlueprintFamilyForEvent'
import { useLiveEventTagGroupWithEventTypeId } from '../../../../hooks/useLiveEventTagGroups'
import { TrackingEventTimelineItem } from '../../../../types/LiveEvents'
import { EventKPIIndicator } from '../../../EventKPIIndicator/EventKPIIndicator'
import { PriceRangeIndicator } from '../../../PriceRangeIndicator/PriceRangeIndicator'

const TAXONOMY_COLUMN_GUTTER_PX = 16

type TaxonomyColumn = {
  key: 'category' | 'type' | 'blueprintFamily' | 'blueprint'
  label: string
  value?: string
  adornment?: ReactNode
}

// Category · Type · Blueprint Family · Blueprint as labelled columns under the
// title. Nothing in the hover box is clickable, so the values are plain ink.
const TooltipTaxonomyRow: FC<{ columns: TaxonomyColumn[]; dotColor: string }> = ({ columns, dotColor }) => {
  const visibleColumns = columns.filter((column) => column.value)
  if (visibleColumns.length === 0) return null

  return (
    // Every column carries its left rule and gutter; the row is pulled left by
    // exactly that much and clipped. So when a narrow viewport wraps the row,
    // each new line still starts flush with the title, with no rule in front of it.
    <Box sx={{ mt: 1.5, overflow: 'hidden' }}>
      <Box data-testid="tooltip-taxonomy-row" sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 1, ml: `-${TAXONOMY_COLUMN_GUTTER_PX + 1}px` }}>
        {visibleColumns.map((column) => (
          <Box
            key={column.key}
            data-testid={`tooltip-taxonomy-${column.key}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '3px',
              minWidth: 0,
              pl: `${TAXONOMY_COLUMN_GUTTER_PX}px`,
              pr: `${TAXONOMY_COLUMN_GUTTER_PX}px`,
              borderLeft: `1px solid ${colors.ruleSubtle}`,
              '&:last-of-type': { pr: 0 },
            }}
          >
            <Typography
              component="span"
              sx={{ fontSize: '0.625rem', lineHeight: 1.2, textTransform: 'uppercase', letterSpacing: '0.08em', color: colors.textMuted }}
            >
              {column.label}
            </Typography>
            <Typography
              component="span"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.8125rem', lineHeight: 1.3, color: 'text.primary', overflowWrap: 'anywhere' }}
            >
              {column.key === 'category' && (
                <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
              )}
              {column.value}
              {column.adornment}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

interface LiveEventInfoTooltipContentProps {
  item: TrackingEventTimelineItem
  itemContext: any
  descriptionRawHtml?: string | null
  bgColor: string
  newItem: boolean
  firstInstance: boolean
  mechanicChange: boolean
  inactive?: boolean
  onViewDetails?: () => void
}

export const LiveEventInfoTooltipContent: FC<LiveEventInfoTooltipContentProps> = ({
  item,
  itemContext,
  descriptionRawHtml,
  bgColor,
  newItem,
  firstInstance,
  mechanicChange,
  inactive,
  onViewDetails,
}) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const isInternalUser = useInternalAccessCheck()

  const displayExperimentalFeatureMotivations = useDisplayExperimentalFeature(ExperimentalFeature.LiveEventMotivations) // NOTE: Remove check when we want to release Live Event Motivations
  const priceRange = liveEventPriceRange(item.trackingEvent?.pricePoints, item.trackedGame?.game?.currencies)
  const gameLevelBlueprintId = item.trackingEvent?.blueprintEvolutionData?.blueprintId
  const isBlueprintDiscontinued = Boolean(item.trackedGame?.blueprints?.find((blueprint) => blueprint.blueprintId === gameLevelBlueprintId)?.discontinued)
  const blueprintDescription = item.trackingEvent?.blueprintEvolutionData?.descriptions?.[userLanguage]
    ? item.trackingEvent?.blueprintEvolutionData?.descriptions?.[userLanguage]
    : item.trackingEvent?.blueprintEvolutionData?.descriptions?.['en']

  const eventTypeId = item.trackingEvent?.typeId
  const tagGroup = useLiveEventTagGroupWithEventTypeId(eventTypeId || '')
  const eventTypeName = eventTypeId ? languageService.getTranslation('tags', eventTypeId) : ''
  const blueprintName = useLiveEventBlueprintName({ blueprintId: gameLevelBlueprintId, trackedGame: item.trackedGame })
  const { familyName } = useBlueprintFamilyForEvent({ trackedGame: item.trackedGame, gameLevelBlueprintId })
  const taxonomyColumns: TaxonomyColumn[] = [
    { key: 'category', label: t('common:category'), value: tagGroup?.name },
    { key: 'type', label: t('live-events:event_type_label'), value: eventTypeName },
    { key: 'blueprintFamily', label: t('live-events:market_level_blueprint'), value: familyName },
    {
      key: 'blueprint',
      label: t('live-events:blueprint'),
      value: blueprintName,
      adornment: isBlueprintDiscontinued ? (
        <GRTooltip content={t('live-events:live_event_evolution_type_discontinued')} styles={{ display: 'inline-flex', cursor: 'help' }}>
          <Block color="error" sx={{ fontSize: 16 }} />
        </GRTooltip>
      ) : undefined,
    },
  ]
  const showKpis = Boolean(item.performanceChanges && item.end_time < Date.now() && item.trackedGame?.game?.isMobileGame())

  if (!item.trackingEvent) {
    return (
      <Box>
        <CircularProgress size={24} />
      </Box>
    )
  }

  const showWipMessage = item.trackingEvent.active === false && !isInternalUser

  return (
    // Cap the whole tooltip at the same 600px the description Grid below already uses. The blueprint
    // "Change in this version" paragraph and its divider sit outside that Grid, so once their own
    // width cap was removed (to kill the right-side gap) they had no bound and stretched the entire
    // tooltip almost full-screen. Bounding the root keeps that paragraph full-width (no gap) while
    // stopping the tooltip from ballooning. (GR-2957)
    <Box sx={{ fontSize: '14px', maxWidth: 600 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ minWidth: 0 }}>
          <Typography component="div" sx={{ fontSize: '1.5rem', fontWeight: 'bold', lineHeight: 1.1, color: 'text.primary', overflowWrap: 'break-word' }}>
            {newItem && <FiberNew sx={{ position: 'relative', fontSize: '20px', verticalAlign: 'middle', top: '-1px', mr: 0.5 }} />}
            {mechanicChange && (
              <BuildCircle sx={{ position: 'relative', fontSize: '20px', verticalAlign: 'middle', top: '-2px', mr: 0.5, color: 'info.main' }} />
            )}
            {itemContext.title}
            {inactive && <Chip label={t('live-events:event_inactive')} color="warning" size="small" sx={{ verticalAlign: 'middle', ml: 1, mt: '-2px' }} />}
          </Typography>
          <TooltipTaxonomyRow columns={taxonomyColumns} dotColor={bgColor} />
        </Box>
        {onViewDetails && (
          <Button variant="outlined" size="small" onClick={onViewDetails} sx={{ ml: 2, flexShrink: 0 }}>
            {t('common:view_details')}
          </Button>
        )}
      </Stack>

      <Divider sx={{ my: '18px', borderColor: colors.ruleSubtle }} />

      <Grid container spacing={2} sx={{ color: newItem ? bgColor : 'secondary.main' }} maxWidth={600}>
        <Grid item xs>
          {showWipMessage ? (
            <Typography variant="body2" sx={{ mb: 1.5, fontStyle: 'italic' }}>
              {t('live-events:event_wip_tooltip')}
            </Typography>
          ) : (
            descriptionRawHtml && (
              <Box mb={1.5} sx={{ '& *': { fontSize: '14px !important' } }}>
                <HtmlContentParser rawHtml={descriptionRawHtml} parserOptions={liveEventReviewModalParserOptions(item.trackingEvent.gameId || '')} />
              </Box>
            )
          )}

          <PriceRangeIndicator priceRange={priceRange} sx={{ mb: 1 }} />

          {displayExperimentalFeatureMotivations && item.trackingEvent.motivations?.motivations && item.trackingEvent.motivations?.motivations.length > 0 && (
            <Box className="LiveEventsCalendar__ItemMotivations" style={{ marginBottom: '-8px' }}>
              <Divider sx={{ mt: 2, mb: 1 }}>{t('motivations:motivations')}</Divider>
              {item.trackingEvent.motivations?.motivations.map((motivation) => {
                return <MotivationTypeBadge key={motivation.key} type={motivation.key} description={motivation.description} alignLeft />
              })}
            </Box>
          )}
        </Grid>

        {(showKpis || item.trackingEvent.screenshotUrl) && (
          <Grid item>
            <Stack spacing={1} alignItems="center" sx={{ maxWidth: 200 }}>
              {showKpis && item.performanceChanges && (
                <EventKPIIndicator
                  revenueChange={item.performanceChanges.REVENUE_DAY_1_CHANGE}
                  downloadChange={item.performanceChanges.DOWNLOAD_DAY_1_CHANGE}
                  dauChange={item.performanceChanges.DAU_DAY_1_CHANGE}
                  startTime={item.start_time}
                  endTime={item.end_time}
                  eventEnded={item.performanceEnded ? true : undefined}
                  hideYear
                  hideStatusChip
                  showComparisonRange
                  showAllKpiChips
                />
              )}
              {item.trackingEvent.screenshotUrl && (
                <AnimatedImage
                  className="LiveEventsCalendar__ItemScreenshot"
                  url={item.trackingEvent.screenshotUrl}
                  alt="Screenshot"
                  imgStyle={{ maxWidth: '200px', maxHeight: '200px' }}
                  autoPlay
                />
              )}
            </Stack>
          </Grid>
        )}
      </Grid>

      {blueprintDescription && blueprintDescription.trim().length > 0 && (
        <>
          <Box textAlign={'center'} sx={{ mt: 2 }}>
            <Divider style={{ margin: '10px 0px 10px' }}>{t('live-events:change_in_this_version')}</Divider>
          </Box>

          <Typography variant="body2" component="div" sx={{ mt: 1, mb: 2, fontStyle: 'italic', textAlign: 'justify' }}>
            <HtmlContentParser rawHtml={blueprintDescription} parserOptions={liveEventReviewModalParserOptions(item.trackedGame?.game?.id || '')} />
          </Typography>
        </>
      )}

      {newItem && (
        <Box textAlign={'center'}>
          <Divider style={{ margin: '10px 0px 10px' }} />
          <strong>
            <span style={{ fontSize: '13px' }}>{t('live-events:event_type_first_appearance')}</span>
          </strong>
        </Box>
      )}

      {firstInstance && (
        <Box textAlign={'center'}>
          <Divider style={{ margin: '10px 0px 10px' }} />
          <strong>
            <span style={{ fontSize: '13px' }}>{t('live-events:event_blueprint_first_instance_seen')}</span>
          </strong>
        </Box>
      )}

      {mechanicChange && (
        <Box textAlign={'center'}>
          <Divider style={{ margin: '10px 0px 10px' }} />
          <strong>
            <span style={{ fontSize: '13px' }}>{t('live-events:event_mechanic_change')}</span>
          </strong>
        </Box>
      )}
    </Box>
  )
}
